import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'hooks/use-redux'
import type { ShortlistSection as TShortlistSection } from 'types/shortlist'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import {
  removeShortlistSection,
  updateShortlistSection,
} from '@disco/state-management/actions/ShortlistActions'
import { SECTION } from '@disco/constants/ItemTypes'
import ShortlistSection from 'components/shortlist/shortlist-section'
import Draggable from 'components/ui/draggable'
type Props = {
  section: TShortlistSection
  shortlistId: string
}

const ShortlistSectionItem = ({ section, shortlistId }: Props) => {
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement | null | undefined>(null)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [name, setName] = useState<string>(section.name)
  useEffect(() => {
    setName(section.name)
  }, [section.name])
  const focusInput = useCallback(() => {
    if (!isFocused && inputRef.current) {
      inputRef.current.focus()
    }
  }, [inputRef, isFocused])
  const handleBlur = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const name = event.target.value
      setIsFocused(false)

      if (name !== section.name) {
        dispatch(updateShortlistSection(shortlistId, section.id, name))
      }
    },
    [dispatch, section, shortlistId],
  )
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setName(event.target.value)
    },
    [],
  )
  const handleDragStart = useCallback(() => {
    dispatch(resetSelection())
  }, [dispatch])
  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])
  const handleRemove = useCallback(() => {
    dispatch(removeShortlistSection(shortlistId, section.id))
  }, [dispatch, section.id, shortlistId])
  return (
    <Draggable
      id={`${shortlistId}-section-${section.id}`}
      data={{
        section,
        shortlistId,
      }}
      type={SECTION}
      onDragStart={handleDragStart}
    >
      <ShortlistSection
        name={name}
        ref={inputRef}
        onBlur={handleBlur}
        onChange={handleChange}
        onClick={focusInput}
        onFocus={handleFocus}
        onRemove={handleRemove}
      />
    </Draggable>
  )
}

export default React.memo<Props>(ShortlistSectionItem)
